import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import TagList from './TagList';
import PageLandscape from '../brand/PageLandscape';

// Bottom edge of the top bar: what the header's first part has to disappear
// behind for the title row to take over the spot.
const TOP_BAR_HEIGHT = 68;

// Shortest scroll the title is allowed to shrink over, for pages that carry
// nothing under the title row.
const MIN_SHRINK_DISTANCE = 40;

function isShown(element) {
  return Boolean(element) && element.offsetHeight > 0;
}

/*
 * The title row sticks under the top bar as soon as the part above it - the
 * eyebrow, or the row itself on a page without one - is gone behind the bar.
 * Once the last part under the row starts going under it as well, the title
 * shrinks with the scroll, from the hero size down to the toolbar size; at
 * the smallest size the row gets a hairline along its bottom edge.
 *
 * Both points are scroll positions, worked out once from the header at its
 * full size, so the title's own shrinking - which moves everything under it
 * up a little - cannot feed back into them. They are measured again whenever
 * the page is scrolled above the shrinking point, where the header is at
 * full size anyway. Parts a page left empty are not displayed at all, so the
 * measurements skip them.
 */
function usePageHeaderScroll({ eyebrowRef, rowRef, tailRefs, condensed }) {
  useEffect(() => {
    let points = null;
    let frame = null;

    const measure = () => {
      const eyebrow = eyebrowRef.current;
      const row = rowRef.current;
      const top = window.scrollY;

      if (!condensed) {
        // Measure the header at its full size: the title may still carry the
        // previous frame's shrinking, which would put the points too high.
        row.style.setProperty('--page-title-shrink', 0);
      }

      const rowBox = row.getBoundingClientRect();
      const stuckBottom = TOP_BAR_HEIGHT + rowBox.height;

      // What stands above the row, and the last thing under it that a page
      // actually filled in.
      const lead = isShown(eyebrow) ? eyebrow.getBoundingClientRect().bottom : rowBox.top;
      const tail = tailRefs
        .map((ref) => ref.current)
        .filter(isShown)
        .pop();

      const stickAt = lead + top - TOP_BAR_HEIGHT;

      if (!tail) {
        // Nothing under the title: it shrinks right after the row sticks.
        points = {
          stickAt,
          shrinkFrom: stickAt,
          shrinkOver: MIN_SHRINK_DISTANCE,
        };

        return;
      }

      const tailBox = tail.getBoundingClientRect();
      const tailMargin = parseFloat(getComputedStyle(tail).marginBottom) || 0;

      points = {
        stickAt,
        // The last part reaches the bottom edge of the stuck row ...
        shrinkFrom: tailBox.top + top - stuckBottom,
        // ... and the title is at its smallest once it has passed under it.
        shrinkOver: Math.max(MIN_SHRINK_DISTANCE, tailBox.height + tailMargin),
      };
    };

    const update = () => {
      frame = null;

      const row = rowRef.current;

      if (!row) {
        return;
      }

      const top = window.scrollY;

      if (!points || top <= points.shrinkFrom) {
        measure();
      }

      // A condensed header opens in its final form - small, stuck, with the
      // hairline: there is nothing under the title to scroll past first.
      const progress = condensed
        ? 1
        : Math.min(
          1,
          Math.max(0, (top - points.shrinkFrom) / points.shrinkOver),
        );

      row.classList.toggle('sticky', condensed || top >= points.stickAt);
      row.style.setProperty('--page-title-shrink', progress);
      row.classList.toggle('is-condensed', progress >= 1);
    };

    const handleScroll = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(update);
      }
    };

    const handleResize = () => {
      points = null;
      handleScroll();
    };

    update();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);

      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
    // tailRefs is built once per render and always holds the same refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eyebrowRef, rowRef, condensed]);
}

// The areas behind an article's summary and meta line (landscape="behind"),
// in a box that runs from the top of the summary down to halfway between the
// meta line and the tags; the tags stay on white. The areas rise gently to
// the right. The Wasserblau area always lies under the whole meta line (white
// text there, see index.css) - its lowest edge sits just above the line,
// wherever the line ends up - and the lighter areas share the room above it.
// None of them reaches up into the summary: they start below its last line
// (`textBottom`), so the summary always stands on white and the areas push
// in from below, as the Landesmarke asks - grey text on Himmelblau would not
// be readable. `metaTop` and `textBottom` are in percent of the box.
function behindBands(metaTop, textBottom = 0) {
  const base = Math.max(metaTop - 4, 20);
  const start = Math.min(textBottom, base - 12);
  const range = base - start;
  const deep = Math.min(14, range * 0.45);

  return [
    { color: 'var(--mv-eisblau-50)', top: start + range * 0.15, amplitude: range * 0.55 },
    { color: 'var(--mv-himmelblau)', top: start + range * 0.45, amplitude: range * 0.45 },
    { color: 'var(--mv-wasserblau)', top: base - deep, amplitude: deep },
  ];
}

// Where that box ends, in percent of the lower header block, and where the
// meta line starts inside it; kept measured as the header reflows (window
// width, font size, tags wrapping).
function useLandscapeSplit({ enabled, lowerRef, descriptionRef, metaRef, tagsRef }) {
  const [split, setSplit] = useState(null);

  useLayoutEffect(() => {
    const lower = lowerRef.current;

    if (!enabled || !lower) {
      return undefined;
    }

    function measure() {
      const box = lower.getBoundingClientRect();
      const meta = metaRef.current;
      const tags = tagsRef.current;

      if (!box.height || !isShown(meta) || !isShown(tags)) {
        setSplit(null);
        return;
      }

      const metaBox = meta.getBoundingClientRect();
      const gap = (metaBox.bottom + tags.getBoundingClientRect().top) / 2;
      const height = gap - box.top;
      const description = descriptionRef.current;
      const textBottom = isShown(description)
        ? description.getBoundingClientRect().bottom - box.top
        : 0;

      setSplit({
        split: (height / box.height) * 100,
        metaTop: ((metaBox.top - box.top) / height) * 100,
        textBottom: (textBottom / height) * 100,
      });
    }

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(lower);

    return () => observer.disconnect();
  }, [enabled, lowerRef, descriptionRef, metaRef, tagsRef]);

  return split;
}

/**
 * The header at the top of every page: an eyebrow, the title with the page's
 * actions beside it, a description, a meta line (`meta`: {key, icon, text,
 * href?}) and a row of tags.
 *
 * Every page has the same parts in the same places; a part a page has nothing
 * for stays empty and is not displayed. `titleId` names the h1 for an
 * aria-labelledby elsewhere.
 *
 * `condensed` is for the pages that carry nothing under the title - the
 * lists, the editors: their title opens in the form the others only reach
 * after scrolling, small and stuck, instead of waiting for a scroll that
 * would shrink it.
 */
function PageHeader({
  title,
  titleId,
  eyebrow,
  department = null,
  description,
  // A short line right next to the title, e.g. "Hier gibt es noch nichts".
  note = null,
  actions = null,
  meta = [],
  tags = [],
  condensed = false,
  landscape = true,
}) {
  const eyebrowRef = useRef(null);
  const rowRef = useRef(null);
  const descriptionRef = useRef(null);
  const metaRef = useRef(null);
  const tagsRef = useRef(null);

  const lowerRef = useRef(null);

  usePageHeaderScroll({
    eyebrowRef,
    rowRef,
    tailRefs: [descriptionRef, metaRef, tagsRef],
    condensed,
  });

  const landscapeSplit = useLandscapeSplit({
    enabled: landscape === 'behind',
    lowerRef,
    descriptionRef,
    metaRef,
    tagsRef,
  });

  const lower = (
    <>
      <p ref={descriptionRef} className="page-header__description">
        {description}
      </p>

      <div className="page-header__details-row">
        {department && (
          <ul className=" page-header__department page-header__meta">
            <li>
              {department}
            </li>
          </ul>
        )}

        {meta.length > 0 && (
          <ul ref={metaRef} className="page-header__meta">
            {meta.map((item) => (
              <li key={item.key}>
                <i className={item.icon} aria-hidden="true" />
                {item.href ? (
                  <a href={item.href}>{item.text}</a>
                ) : (
                  item.text
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <TagList
        ref={tagsRef}
        tags={tags}
        className="page-header__tags"
      />
    </>
  );

  return (
    <header className="page-header">
      <p ref={eyebrowRef} className="page-eyebrow">{eyebrow}</p>

      <div ref={rowRef} className="page-header__row">
        <div className="page-header__title-group">
          <h1 id={titleId}>{title}</h1>
        </div>
        {note && (
          <span className="page-header__note">
            <i className="fa-regular fa-folder-open" aria-hidden="true" />
            {note}
          </span>
        )}
        {actions && (
          <div className="page-header__right">
            {actions}
          </div>
        )}
      </div>

      {landscape === 'behind' ? (
        // Articles and news: the MV landscape lies behind the summary and
        // meta line instead of forming its own strip under the header.
        // Without a summary the block starts at the meta line; it then gets
        // room above it so the areas can still rise (see index.css).
        <div
          ref={lowerRef}
          className={[
            'page-header__lower',
            !description && 'page-header__lower--no-summary',
          ].filter(Boolean).join(' ')}
          style={landscapeSplit === null
            ? undefined
            : { '--landscape-split': `${landscapeSplit.split}%` }}
        >
          {lower}
          <PageLandscape
            className="page-landscape--behind"
            bands={behindBands(
              landscapeSplit?.metaTop ?? 60,
              landscapeSplit?.textBottom ?? 0,
            )}
          />
        </div>
      ) : (
        <>
          {lower}
          {landscape && <PageLandscape className="page-landscape--header" />}
        </>
      )}
    </header>
  );
}

export default PageHeader;
